/**
 * @lodex-pro/web-portal/routes/Tanks — BASIC+ tank levels + 90-day trends.
 *
 * Phase 15 / Task 15.4 (DEC-153) — grid of tank cards. Each card shows current
 * level (from most recent haul_capture 'after' reading), capacity, fill %, and
 * a Recharts AreaChart of the 90-day sawtooth (before/after readings per haul).
 * Phase 13 / 13.1 adds the fill rate and predicted-full date; 13.1b (DEC-351) leads
 * with the ESTIMATED current volume, always labelled, unless the hauler turned estimates off.
 * A plan without tank levels renders the PremiumStub instead.
 */

import React from 'react';
import { Area, AreaChart, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatTankReading, type TankLatestReading } from '@lodex-pro/shared/tank-level-format';
import { isHistoryTooShort } from '@lodex-pro/shared/tank-fill-rate';
import { usePortalMe, usePortalTanks } from '../hooks/useOperatorPortal';
import { TankReadingPanel } from './TankReadingPanel';
import { PortalDataError } from '../components/PortalDataError';
import { ScopeNotice } from '../components/ScopeNotice';
import { PremiumStub } from './PremiumStub';
import { portalLacks } from '../nav/portal-features';

function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function toNumber(s: string | null | undefined): number {
  if (s === null || s === undefined || s === '') return 0;
  const n = Number(s);
  return Number.isNaN(n) ? 0 : n;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Phase 13 / 13.1b (DEC-351) — the card headline. With estimates on and an estimate
 * available it leads with the ESTIMATED current volume, always labelled "(est.)", and when
 * the tank will be full. Otherwise it shows the last recorded level, or says there is none.
 *
 * 13.8 (D-23c): a latest reading with no barrels (inches on a tank with no size of its own) is
 * shown RAW, "8 ft 4 in, size unknown", when nothing recorded is newer. Never "~N BBL (est.)"
 * built from it: barrels come only from the tank's own size, in the database.
 */
export function tankHeadline(
  t: {
    estimatesEnabled: boolean;
    estimatedCurrentBbl: number | null;
    currentLevelBbl: string | null;
    predictedFullAt: string | null;
    currentLevelAt?: string | null;
    latestReading?: TankLatestReading | null;
    historySpanHours?: number | null;
  },
  now: number = Date.now(),
): string {
  if (t.estimatesEnabled && t.estimatedCurrentBbl !== null) {
    const vol = `~${Math.round(t.estimatedCurrentBbl).toLocaleString()} BBL (est.)`;
    // #1013 (shared with the Tanks tab and the pumper view): no full time from too little history.
    if (t.predictedFullAt === null || isHistoryTooShort(t.historySpanHours)) return vol;
    const fullMs = Date.parse(t.predictedFullAt);
    if (Number.isNaN(fullMs)) return vol;
    const days = (fullMs - now) / MS_PER_DAY;
    if (days <= 0) return `${vol} · may already be full`;
    if (days < 1) return `${vol} · full within a day`;
    const n = Math.round(days);
    return `${vol} · full in about ${n} day${n === 1 ? '' : 's'}`;
  }
  const raw = t.latestReading ?? null;
  if (
    raw !== null &&
    raw.volumeBbl === null &&
    raw.levelIn !== null &&
    (t.currentLevelBbl === null ||
      t.currentLevelAt == null ||
      Date.parse(raw.readAt) >= Date.parse(t.currentLevelAt))
  ) {
    return formatTankReading(raw);
  }
  if (t.currentLevelBbl !== null) {
    return `${toNumber(t.currentLevelBbl).toLocaleString()} BBL (last recorded)`;
  }
  return 'No recorded level';
}

/**
 * The smaller line under the bar. Null rate means "not enough history"; the API never
 * guesses, so neither does this. With estimates off there is no rate to show at all.
 * 13.10: readings covering under MIN_HISTORY_SPAN_HOURS show no rate either (#1013): dense
 * machine readings give the API a rate within hours (DEC-370), too soon to show.
 */
export function fillRateText(
  estimatesEnabled: boolean,
  fillRateBpd: number | null,
  historySpanHours?: number | null,
): string {
  if (!estimatesEnabled) return 'Showing recorded levels only';
  if (fillRateBpd === null || isHistoryTooShort(historySpanHours)) {
    return 'Fill rate: not enough haul history yet';
  }
  return `Fills about ${fillRateBpd.toLocaleString()} BBL/day (est.)`;
}

export function Tanks(): React.JSX.Element {
  const me = usePortalMe();
  const { data, isLoading, isError, error } = usePortalTanks();

  // OPP PR 6 — the plan's OP_TANK_LEVELS feature, as the API gates it.
  if (portalLacks(me.data, 'OP_TANK_LEVELS')) {
    return <PremiumStub feature="tanks" />;
  }

  const tanks = data?.tanks ?? [];

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '12px' }}>Tanks</h1>
      <ScopeNotice me={me.data} />

      {isLoading && <p role="status">Loading tanks…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load tanks." />}

      {data !== undefined && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
          }}
        >
          {tanks.length === 0 ? (
            <p role="status" style={{ color: 'var(--color-text-tertiary)' }}>
              No tanks linked to your operator.
            </p>
          ) : (
            tanks.map((t) => {
              const capacity = toNumber(t.capacityBbl);
              // DEC-351 — the bar follows the headline: the estimate when there is one.
              const shown =
                t.estimatesEnabled && t.estimatedCurrentBbl !== null
                  ? t.estimatedCurrentBbl
                  : toNumber(t.currentLevelBbl);
              const fillPct =
                capacity > 0 ? Math.min(100, Math.max(0, (shown / capacity) * 100)) : 0;
              const hasEstimatedPoints = t.timeseries.some((p) => p.estimated);
              const timeseriesForChart = t.timeseries.map((p) => ({
                at: p.at,
                bbl: toNumber(p.bbl),
              }));
              return (
                <article
                  key={t.tankId}
                  aria-label={`Tank ${t.tankName}`}
                  style={{
                    padding: '16px',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-card)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: '8px',
                    }}
                  >
                    <h2 style={{ fontSize: '15px', margin: 0 }}>{t.tankName}</h2>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                      {t.siteName ?? ''}
                    </span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span aria-label="Tank level" style={{ fontSize: '20px', fontWeight: 700 }}>
                      {tankHeadline(t)}
                    </span>
                    {capacity > 0 && (
                      <span
                        style={{
                          color: 'var(--color-text-tertiary)',
                          marginLeft: '6px',
                          fontSize: '13px',
                        }}
                      >
                        of {capacity.toLocaleString()} BBL
                      </span>
                    )}
                  </div>
                  <div
                    aria-label="Fill percentage"
                    style={{
                      height: '8px',
                      background: 'var(--color-bg-tertiary)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: `${fillPct}%`,
                        height: '100%',
                        background:
                          fillPct > 80 ? 'var(--color-tone-orange)' : 'var(--color-accent-blue)',
                      }}
                    />
                  </div>
                  <p
                    aria-label="Fill rate"
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-tertiary)',
                      margin: '0 0 12px',
                    }}
                  >
                    {fillRateText(t.estimatesEnabled, t.fillRateBpd, t.historySpanHours)}
                  </p>
                  {/* 13.2b — a reading the company gauged, shared with the tank's haulers. */}
                  <div style={{ marginBottom: '12px' }}>
                    <TankReadingPanel tank={t} />
                  </div>
                  <div style={{ height: '140px' }}>
                    {timeseriesForChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeseriesForChart}>
                          <XAxis
                            dataKey="at"
                            stroke="var(--color-text-tertiary)"
                            fontSize={10}
                            tickFormatter={formatDate}
                          />
                          <YAxis stroke="var(--color-text-tertiary)" fontSize={10} />
                          <Tooltip
                            contentStyle={{
                              background: 'var(--color-bg-secondary)',
                              border: '1px solid var(--color-border-secondary)',
                            }}
                            labelStyle={{ color: 'var(--color-text-primary)' }}
                            labelFormatter={(l): string => formatDate(String(l))}
                          />
                          <Area
                            type="stepAfter"
                            dataKey="bbl"
                            stroke="var(--color-accent-blue)"
                            fill="rgba(123, 150, 232, 0.25)"
                            fillOpacity={0.4}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <p
                        role="status"
                        style={{ color: 'var(--color-text-tertiary)', fontSize: '12px' }}
                      >
                        No trend data.
                      </p>
                    )}
                  </div>
                  {hasEstimatedPoints && (
                    <p
                      style={{
                        color: 'var(--color-text-tertiary)',
                        fontSize: '11px',
                        margin: '6px 0 0',
                      }}
                    >
                      Includes levels estimated from gauge readings.
                    </p>
                  )}
                </article>
              );
            })
          )}
        </div>
      )}
    </main>
  );
}
